import { useAuth } from '../../auth/AuthContext'
import { getCoachEarnings } from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import {
  Badge,
  Card,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  Section,
} from '../../components/ui'
import { formatDay, formatMoney, formatMonth, humanize } from '../../lib/format'

/**
 * Earnings.
 *
 * The shadow-month gate lives in the database: earnings.is_visible_to_coach
 * defaults to false and the coach SELECT policy requires it. So this screen is
 * complete and correct, and until Pascal signs the pay grid off it truthfully
 * shows nothing but the reason. That is the point - a wrong number here costs
 * more trust than a missing one.
 */
export function CoachEarnings() {
  const { profile } = useAuth()
  const coachId = profile?.id ?? ''
  const { data, error, loading, reload } = useAsync(() => getCoachEarnings(coachId), [coachId])

  if (loading) return <Loading label="Loading your earnings" />
  if (error) return <ErrorState message={error} onRetry={reload} />

  const summary = data
  const hasRows = (summary?.months.length ?? 0) > 0

  return (
    <>
      <PageHeader title="My earnings" subtitle="What each event paid, and why." />

      {!hasRows ? (
        <Notice tone="info" title="Not open yet">
          Your earnings will appear here once the pay grid is confirmed with Pascal and one month
          has been checked line by line against his sheet. Nothing is hidden from you, the numbers
          simply are not final yet, and a wrong number is worse than none.
          <p className="small" style={{ marginTop: 'var(--sp-3)' }}>
            Your events are all recorded in the meantime, so nothing is lost.
          </p>
        </Notice>
      ) : null}

      {summary?.containsPlaceholderRates ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="warning" title="Provisional rates">
            Some of these lines still use a placeholder rate. Treat them as an estimate until
            Pascal confirms.
          </Notice>
        </div>
      ) : null}

      {summary?.months.map((month) => (
        <Section key={month.period_month} title={formatMonth(month.period_month)}>
          <Card tone="muted">
            <div className="row row--between">
              <span className="muted">Month total</span>
              <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                {formatMoney(month.total_aed)}
              </span>
            </div>
          </Card>

          <div className="stack" style={{ marginTop: 'var(--sp-3)' }}>
            {month.rows.map((row) => (
              <Card key={row.id}>
                <div className="row row--between">
                  <div style={{ minWidth: 0 }}>
                    <p className="card__title">{row.event.title}</p>
                    <p className="small muted" style={{ marginTop: 2 }}>
                      {formatDay(row.event.starts_at)} &middot; {row.event.service.name}
                    </p>
                  </div>
                  <span style={{ fontWeight: 700 }}>{formatMoney(row.amount_aed)}</span>
                </div>

                {row.breakdown ? (
                  <div className="card__foot" style={{ display: 'block' }}>
                    {/*
                      calculate_event_pay omits the rate fields entirely when no
                      pay_rates row matched, so pay_rate_id is the honest test.
                      Without it, this line would read "Base AED 0.00 plus AED
                      0.00 per hour" and look like a real, wrong number.
                    */}
                    {row.breakdown.pay_rate_id ? (
                      <p className="faint">
                        Base {formatMoney(row.breakdown.base_amount_aed)} plus{' '}
                        {formatMoney(row.breakdown.hourly_rate_aed)} per hour for{' '}
                        {row.breakdown.hours} hours
                        {(row.breakdown.travel_allowance_aed ?? 0) > 0
                          ? `, plus ${formatMoney(row.breakdown.travel_allowance_aed)} travel`
                          : ''}
                        .
                      </p>
                    ) : (
                      <p className="faint">
                        No pay rate is set for this event type yet, so this line is not final.
                        Michel is confirming it.
                      </p>
                    )}
                    <div className="row row--wrap" style={{ marginTop: 'var(--sp-2)' }}>
                      <Badge tone={row.status === 'paid' ? 'positive' : 'neutral'}>
                        {humanize(row.status)}
                      </Badge>
                      <Badge tone="info">{humanize(row.breakdown.seniority)}</Badge>
                      <Badge tone="info">{humanize(row.breakdown.location_zone)}</Badge>
                      {row.breakdown.is_placeholder ? (
                        <Badge tone="warning">Provisional rate</Badge>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </Card>
            ))}
          </div>
        </Section>
      ))}
    </>
  )
}
